import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export default function NotFound404() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); navigate('/'); }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Outfit', sans-serif",
      position: 'relative',
      overflow: 'hidden',
      padding: '20px',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,700;0,9..144,900;1,9..144,700&family=Outfit:wght@300;400;500;600;700&display=swap');

        @keyframes floatPlate {
          0%, 100% { transform: translate(-50%, -50%) translateY(0px); }
          50%       { transform: translate(-50%, -50%) translateY(-12px); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .nf-fade-1 { animation: fadeUp 0.6s ease 0.0s both; }
        .nf-fade-2 { animation: fadeUp 0.6s ease 0.1s both; }
        .nf-fade-3 { animation: fadeUp 0.6s ease 0.2s both; }
        .nf-fade-4 { animation: fadeUp 0.6s ease 0.3s both; }
        .nf-fade-5 { animation: fadeUp 0.6s ease 0.4s both; }
        .nf-fade-6 { animation: fadeUp 0.6s ease 0.5s both; }

        .nf-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 13px 28px; border-radius: 100px;
          font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 600;
          cursor: pointer; border: none; text-decoration: none;
          transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1);
        }
        .nf-btn:hover { transform: translateY(-2px); }
        .nf-btn-primary { background: #E8600A; color: white; box-shadow: 0 6px 20px rgba(232,96,10,0.3); }
        .nf-btn-primary:hover { background: #d05508; box-shadow: 0 12px 32px rgba(232,96,10,0.4); }
        .nf-btn-ghost { background: white; color: #3D1F00; border: 1.5px solid #E0D5CC !important; }
        .nf-btn-ghost:hover { border-color: #E8600A !important; color: #E8600A; background: #FFF8F4; }
      `}</style>

      {/* Top accent line */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #E8600A, #F5A623, #E8600A)' }} />

      {/* Subtle dot pattern */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(232,96,10,0.04) 1px, transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }} />

      {/* Glow blobs */}
      <div style={{ position: 'absolute', top: '15%', right: '8%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,96,10,0.05), transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '10%', left: '5%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,166,35,0.05), transparent 70%)', pointerEvents: 'none' }} />

      {/* Main Content */}
      <div style={{ textAlign: 'center', maxWidth: 520, width: '100%', position: 'relative', zIndex: 1 }}>

        {/* 404 + Plate perfectly centered */}
        <div className="nf-fade-1" style={{ position: 'relative', height: 200, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* 404 background text */}
          <div style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 'clamp(120px, 25vw, 200px)',
            fontWeight: 900,
            lineHeight: 1,
            color: '#F5EDE5',
            userSelect: 'none',
            letterSpacing: '-4px',
            position: 'relative',
          }}>404</div>

          {/* Plate centered perfectly over 404 */}
          <div style={{
            position: 'absolute',
            top: '50%', left: '50%',
            animation: 'floatPlate 3.5s ease-in-out infinite',
            fontSize: 80,
            lineHeight: 1,
            filter: 'drop-shadow(0 8px 16px rgba(232,96,10,0.15))',
          }}>🍽️</div>
        </div>

        {/* Orange divider */}
        <div className="nf-fade-2" style={{ width: 48, height: 3, background: 'linear-gradient(90deg, #E8600A, #F5A623)', margin: '0 auto 24px', borderRadius: 2 }} />

        {/* Heading */}
        <h1 className="nf-fade-2" style={{ fontFamily: "'Fraunces', serif", fontSize: 'clamp(26px, 5vw, 38px)', fontWeight: 700, color: '#1C0A00', marginBottom: 14, lineHeight: 1.2 }}>
          This Restaurant<br />
          <span style={{ fontStyle: 'italic', color: '#E8600A' }}>Doesn't Exist</span>
        </h1>

        {/* Slug pill */}
        {slug && (
          <div className="nf-fade-3" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#FFF4EE', border: '1px solid #FFD5BE', borderRadius: 100, padding: '6px 16px', marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: '#C49A6A', fontFamily: 'monospace' }}>swadzone.com/</span>
            <span style={{ fontSize: 13, color: '#E8600A', fontFamily: 'monospace', fontWeight: 700, textDecoration: 'line-through' }}>{slug}</span>
          </div>
        )}

        {/* Description */}
        <p className="nf-fade-3" style={{ fontSize: 15, color: '#7A4A1A', lineHeight: 1.75, marginBottom: 36, fontWeight: 300, maxWidth: 400, margin: '0 auto 36px' }}>
          The restaurant you're looking for hasn't registered on SwadZone yet — or the link might be incorrect.
        </p>

        {/* Buttons */}
        <div className="nf-fade-4" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 40 }}>
          <button className="nf-btn nf-btn-primary" onClick={() => navigate('/')}>
            🏠 Go to SwadZone Home
          </button>
          <button className="nf-btn nf-btn-ghost" onClick={() => navigate(-1)}>
            ← Go Back
          </button>
        </div>

        {/* Countdown */}
        <div className="nf-fade-5" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 32 }}>
          <div style={{ position: 'relative', width: 56, height: 56 }}>
            <svg width="56" height="56" viewBox="0 0 56 56" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="28" cy="28" r="24" fill="none" stroke="#F0E8E0" strokeWidth="3" />
              <circle cx="28" cy="28" r="24" fill="none" stroke="#E8600A" strokeWidth="3"
                strokeDasharray="150.8"
                strokeDashoffset={150.8 * (1 - countdown / 10)}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 700, color: '#E8600A' }}>
              {countdown}
            </div>
          </div>
          <p style={{ fontSize: 12, color: '#C49A6A', letterSpacing: 1, textTransform: 'uppercase' }}>
            Redirecting to home in {countdown}s
          </p>
        </div>

        {/* Owner CTA */}
        <div className="nf-fade-6" style={{ padding: '20px 28px', background: '#FFFAF7', border: '1px solid #F0E0D4', borderRadius: 16 }}>
          <p style={{ fontSize: 13, color: '#9A6A4A', marginBottom: 12 }}>
            🏪 Own a restaurant? List it on SwadZone for free.
          </p>
          <button className="nf-btn" style={{ padding: '9px 22px', fontSize: 13, background: 'transparent', color: '#E8600A', border: '1.5px solid rgba(232,96,10,0.3)', borderRadius: 100, cursor: 'pointer' }}
            onClick={() => navigate('/super-admin/login')}>
            Register Your Restaurant →
          </button>
        </div>

      </div>
    </div>
  );
}
